/**
 * TelegramLinkWidget (TASK-226)
 *
 * Telegram account link widget, lifted from the old IntegrationSettings card.
 * Rendered inside the `telegram_system` channel section of ChannelsPanel.
 *
 * The telegram link API (telegramApi) operates on the CURRENT authenticated
 * user (there is no userId argument). So the widget only exposes the
 * link/unlink actions when it is rendered for the current user:
 *   - self === true  → full widget (initiate / poll / unlink)
 *   - self === false → read-only status only (admin editing someone else)
 *
 * States (self mode):
 *  - loading:    spinner while the initial status is fetched
 *  - not-linked: "Привязать Telegram" button
 *  - linking:    deep link + "Проверить" button, polls every 3s, 15 min timeout
 *  - linked:     "@username привязан" badge + "Отвязать" button
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  Unlink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { telegramApi } from '@/services/api/telegram';

type LinkState = 'loading' | 'not-linked' | 'linking' | 'linked';

interface TelegramLinkWidgetProps {
  /**
   * When true the widget acts on the CURRENT user and renders the full
   * link/unlink flow. When false (admin editing another user) it shows only
   * the read-only link status.
   */
  self?: boolean;
  /**
   * TASK-229: read-only link status of the EDITED user, used when `self` is
   * false. The telegram API only knows the CURRENT user, so for admin view we
   * derive the status from these props instead of calling getStatus().
   */
  linked?: boolean;
  username?: string | null;
}

export function TelegramLinkWidget({
  self = false,
  linked = false,
  username = null,
}: TelegramLinkWidgetProps): JSX.Element {
  const [linkState, setLinkState] = useState<LinkState>('loading');
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [telegramUsername, setTelegramUsername] = useState<string | null>(null);
  const [telegramFirstName, setTelegramFirstName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async (): Promise<boolean> => {
    try {
      const status = await telegramApi.getStatus();
      if (status.linked) {
        setLinkState('linked');
        setTelegramUsername(status.telegram_username || null);
        setTelegramFirstName(status.first_name || null);
        setDeepLink(null);
        stopPolling();
      }
      return status.linked;
    } catch {
      // silently fail during polling
      return false;
    }
  }, [stopPolling]);

  // Check initial status on mount.
  useEffect(() => {
    // Admin editing another user (self === false): the telegram API only knows
    // the CURRENT (logged-in) user, so calling getStatus() here would show the
    // ADMIN's link instead of the edited user's. Derive the read-only status
    // from props and skip the API call entirely (TASK-229).
    if (!self) {
      setLinkState(linked ? 'linked' : 'not-linked');
      setTelegramUsername(username ?? null);
      return () => stopPolling();
    }

    const init = async () => {
      try {
        const status = await telegramApi.getStatus();
        if (status.linked) {
          setLinkState('linked');
          setTelegramUsername(status.telegram_username || null);
          setTelegramFirstName(status.first_name || null);
        } else {
          setLinkState('not-linked');
        }
      } catch {
        setLinkState('not-linked');
      }
    };
    init();
    return () => stopPolling();
  }, [self, linked, username, stopPolling]);

  const handleInitiateLink = async () => {
    setError(null);
    try {
      const result = await telegramApi.initiateLink();
      setDeepLink(result.deep_link);
      setLinkState('linking');

      // Poll status every 3 seconds.
      pollIntervalRef.current = setInterval(async () => {
        const linked = await checkStatus();
        if (linked) {
          stopPolling();
        }
      }, 3000);

      // Stop after 15 minutes.
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setLinkState('not-linked');
        setDeepLink(null);
        setError('Время ожидания истекло. Попробуйте снова.');
      }, 15 * 60 * 1000);
    } catch {
      setError('Не удалось создать ссылку. Попробуйте позже.');
    }
  };

  const handleCheckNow = async () => {
    await checkStatus();
  };

  const handleCancel = () => {
    stopPolling();
    setLinkState('not-linked');
    setDeepLink(null);
    setError(null);
  };

  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      await telegramApi.unlink();
      setLinkState('not-linked');
      setTelegramUsername(null);
      setTelegramFirstName(null);
    } catch {
      setError('Не удалось отвязать аккаунт. Попробуйте позже.');
    } finally {
      setUnlinking(false);
    }
  };

  const displayName = telegramUsername ? `@${telegramUsername}` : telegramFirstName || 'Telegram';

  return (
    <div
      data-testid="telegram-link-widget"
      className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <MessageCircle className="h-4 w-4" />
        Привязка Telegram
      </div>

      {linkState === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Проверка статуса...</span>
        </div>
      )}

      {linkState === 'not-linked' && (
        <div className="space-y-3">
          <div
            data-testid="telegram-link-status"
            className="flex items-center gap-2 text-sm text-muted-foreground">
            <XCircle className="h-4 w-4" />
            Не привязан
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {self && (
            <Button
              type="button"
              size="sm"
              data-testid="telegram-link-initiate"
              onClick={handleInitiateLink}>
              <MessageCircle className="mr-2 h-4 w-4" />
              Привязать Telegram
            </Button>
          )}
        </div>
      )}

      {linkState === 'linking' && self && (
        <div className="space-y-3">
          <div
            data-testid="telegram-link-status"
            className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Ожидание подтверждения в Telegram...</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {deepLink && (
              <Button asChild size="sm">
                <a
                  data-testid="telegram-link-deeplink"
                  href={deepLink}
                  target="_blank"
                  rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Открыть бот в Telegram
                </a>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="telegram-link-check"
              onClick={handleCheckNow}>
              Проверить
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      {linkState === 'linked' && (
        <div className="space-y-3">
          <Badge
            data-testid="telegram-link-status"
            variant="completed"
            className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            {displayName} привязан
          </Badge>
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {self && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="telegram-link-unlink"
                onClick={handleUnlink}
                disabled={unlinking}>
                {unlinking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unlink className="mr-2 h-4 w-4" />
                )}
                Отвязать
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TelegramLinkWidget;
